import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpenCheck,
  BrainCircuit,
  Library,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

export default function LMSAssessmentHub() {
  const navigate = useNavigate();

  const [assessments, setAssessments] = useState([]);
  const [modules, setModules] = useState([]);
  const [banks, setBanks] = useState([]);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [showAssessment, setShowAssessment] = useState(false);
  const [showBank, setShowBank] = useState(false);

  const [assessmentForm, setAssessmentForm] = useState({
    module_id: "",
    title: "",
    pass_mark: 80,
    max_attempts: 3,
  });

  const [bankForm, setBankForm] = useState({
    title: "",
    description: "",
  });

  const load = async () => {
    setError("");

    try {
      const me = await lmsApi.get("/auth/me");

      if (me.data.require_password_change) {
        navigate("/learn/change-password", {
          replace: true,
        });
        return;
      }

      if (!me.data.is_platform_admin) {
        navigate("/learn", {
          replace: true,
        });
        return;
      }

      const [
        assessmentResponse,
        moduleResponse,
        bankResponse,
      ] = await Promise.all([
        lmsApi.get("/assessments/activities"),
        lmsApi.get("/assessments/draft-modules"),
        lmsApi.get("/assessments/banks"),
      ]);

      setAssessments(assessmentResponse.data);
      setModules(moduleResponse.data);
      setBanks(bankResponse.data);

      setAssessmentForm((current) => ({
        ...current,
        module_id:
          current.module_id ||
          moduleResponse.data[0]?.module_id ||
          "",
      }));
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/learn/login", {
          replace: true,
        });
        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load assessment authoring."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftAssessments = useMemo(
    () =>
      assessments.filter(
        (item) => item.version_status === "draft"
      ),
    [assessments]
  );

  const publishedAssessments = useMemo(
    () =>
      assessments.filter(
        (item) =>
          item.version_status === "published"
      ),
    [assessments]
  );

  const createAssessment = async (event) => {
    event.preventDefault();

    setBusy(true);
    setError("");

    try {
      const response = await lmsApi.post(
        `/assessments/modules/${assessmentForm.module_id}/activities`,
        {
          title: assessmentForm.title,

          settings: {
            pass_mark: Number(
              assessmentForm.pass_mark
            ),

            max_attempts: Number(
              assessmentForm.max_attempts
            ),

            randomise_questions: false,
            question_limit: null,
            randomise_options: false,
            feedback_mode:
              "after_each_attempt",
            require_pass: true,
          },
        }
      );

      navigate(
        `/learn/admin/assessments/${response.data.activity_id}`
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The assessment could not be created."
      );
    } finally {
      setBusy(false);
    }
  };

  const createBank = async (event) => {
    event.preventDefault();

    setBusy(true);
    setError("");

    try {
      await lmsApi.post(
        "/assessments/banks",
        {
          title: bankForm.title,
          description:
            bankForm.description || null,
        }
      );

      setBankForm({
        title: "",
        description: "",
      });

      setShowBank(false);

      await load();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The question bank could not be created."
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading assessment authoring…
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="Assessment Authoring | TRAC-Learn"
        description="UKTDA assessment and question-bank authoring."
        path="/learn/admin/assessments"
      />

      <section className="bg-navy py-10 text-white md:py-14">
        <div className="container-rail">
          <button
            type="button"
            onClick={() =>
              navigate(
                "/learn/admin/courses"
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Course authoring
          </button>

          <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <div className="flex items-center gap-2 text-safety">
                <ShieldCheck className="h-5 w-5" />

                <p className="label-eyebrow">
                  UKTDA Super Admin
                </p>
              </div>

              <h1 className="mt-3 font-display text-4xl font-bold md:text-5xl">
                Assessments
              </h1>

              <p className="mt-3 max-w-2xl text-white/70">
                Build scored assessments, define attempt rules and maintain
                reusable UKTDA question banks.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() =>
                  setShowBank(
                    (value) => !value
                  )
                }
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-semibold"
              >
                <Library className="h-4 w-4" />
                New question bank
              </button>

              <button
                type="button"
                onClick={() =>
                  setShowAssessment(
                    (value) => !value
                  )
                }
                className="inline-flex items-center gap-2 rounded-lg bg-safety px-4 py-3 text-sm font-bold"
              >
                <Plus className="h-4 w-4" />
                New assessment
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="min-h-[70vh] bg-slate-50 py-10 md:py-14">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {[
              [
                BrainCircuit,
                "Draft assessments",
                draftAssessments.length,
              ],
              [
                BookOpenCheck,
                "Published assessments",
                publishedAssessments.length,
              ],
              [
                Library,
                "Question banks",
                banks.length,
              ],
            ].map(
              ([Icon, label, value]) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
                    <Icon className="h-5 w-5 text-safety" />
                    {label}
                  </div>

                  <p className="mt-3 font-display text-3xl font-bold text-navy">
                    {value}
                  </p>
                </div>
              )
            )}
          </div>

          {showAssessment && (
            <form
              onSubmit={createAssessment}
              className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8"
            >
              <p className="label-eyebrow text-safety">
                Assessment shell
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Create a draft assessment
              </h2>

              {!modules.length ? (
                <p className="mt-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
                  Create a draft course module first. Assessments always
                  belong to an immutable course version.
                </p>
              ) : (
                <div className="mt-6 grid gap-5 md:grid-cols-2">
                  <label className="text-sm font-semibold text-navy md:col-span-2">
                    Course module

                    <select
                      value={
                        assessmentForm.module_id
                      }
                      onChange={(event) =>
                        setAssessmentForm(
                          (current) => ({
                            ...current,
                            module_id:
                              event.target
                                .value,
                          })
                        )
                      }
                      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-3"
                    >
                      {modules.map(
                        (item) => (
                          <option
                            key={
                              item.module_id
                            }
                            value={
                              item.module_id
                            }
                          >
                            {item.course_title} —
                            v
                            {
                              item.version_number
                            }{" "}
                            —{" "}
                            {
                              item.module_title
                            }
                          </option>
                        )
                      )}
                    </select>
                  </label>

                  <label className="text-sm font-semibold text-navy md:col-span-2">
                    Assessment title

                    <input
                      value={
                        assessmentForm.title
                      }
                      onChange={(event) =>
                        setAssessmentForm(
                          (current) => ({
                            ...current,
                            title:
                              event.target
                                .value,
                          })
                        )
                      }
                      required
                      className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3"
                      placeholder="Final knowledge assessment"
                    />
                  </label>

                  <label className="text-sm font-semibold text-navy">
                    Pass mark (%)

                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={
                        assessmentForm.pass_mark
                      }
                      onChange={(event) =>
                        setAssessmentForm(
                          (current) => ({
                            ...current,
                            pass_mark:
                              event.target
                                .value,
                          })
                        )
                      }
                      className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3"
                    />
                  </label>

                  <label className="text-sm font-semibold text-navy">
                    Maximum attempts

                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={
                        assessmentForm.max_attempts
                      }
                      onChange={(event) =>
                        setAssessmentForm(
                          (current) => ({
                            ...current,
                            max_attempts:
                              event.target
                                .value,
                          })
                        )
                      }
                      className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3"
                    />
                  </label>

                  <div className="flex gap-3 md:col-span-2">
                    <button
                      type="submit"
                      disabled={busy}
                      className="rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                    >
                      Create and edit
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShowAssessment(
                          false
                        )
                      }
                      className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}

          {showBank && (
            <form
              onSubmit={createBank}
              className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8"
            >
              <p className="label-eyebrow text-safety">
                Reusable questions
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Create a question bank
              </h2>

              <div className="mt-6 grid gap-5 md:grid-cols-2">
                <label className="text-sm font-semibold text-navy">
                  Bank title

                  <input
                    value={
                      bankForm.title
                    }
                    onChange={(event) =>
                      setBankForm(
                        (current) => ({
                          ...current,
                          title:
                            event.target
                              .value,
                        })
                      )
                    }
                    required
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3"
                  />
                </label>

                <label className="text-sm font-semibold text-navy">
                  Description

                  <input
                    value={
                      bankForm.description
                    }
                    onChange={(event) =>
                      setBankForm(
                        (current) => ({
                          ...current,
                          description:
                            event.target
                              .value,
                        })
                      )
                    }
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3"
                  />
                </label>

                <div className="flex gap-3 md:col-span-2">
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-50"
                  >
                    Create bank
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setShowBank(false)
                    }
                    className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}

          <div className="mt-10 grid gap-8 xl:grid-cols-[1.4fr_0.6fr]">
            <div>
              <p className="label-eyebrow text-safety">
                Assessment catalogue
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Course assessments
              </h2>

              <div className="mt-6 space-y-4">
                {assessments.length ? (
                  assessments.map(
                    (item) => (
                      <button
                        key={
                          item.activity_id
                        }
                        type="button"
                        onClick={() =>
                          navigate(
                            `/learn/admin/assessments/${item.activity_id}`
                          )
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-safety">
                              {
                                item.course_title
                              }{" "}
                              · v
                              {
                                item.version_number
                              }
                            </p>

                            <h3 className="mt-2 font-display text-2xl font-bold text-navy">
                              {
                                item.title
                              }
                            </h3>

                            <p className="mt-2 text-sm text-slate-500">
                              {
                                item.module_title
                              }{" "}
                              ·{" "}
                              {
                                item.question_count
                              }{" "}
                              questions · Pass{" "}
                              {
                                item.pass_mark
                              }
                              % ·{" "}
                              {
                                item.max_attempts
                              }{" "}
                              attempts
                            </p>
                          </div>

                          <span
                            className={`rounded-full px-3 py-1 text-xs font-bold ${
                              item.version_status ===
                              "published"
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {
                              item.version_status
                            }
                          </span>
                        </div>
                      </button>
                    )
                  )
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600">
                    No assessments have been created yet.
                  </div>
                )}
              </div>
            </div>

            <aside>
              <p className="label-eyebrow text-safety">
                Question library
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Banks
              </h2>

              <div className="mt-6 space-y-3">
                {banks.length ? (
                  banks.map((bank) => (
                    <div
                      key={bank.id}
                      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                    >
                      <div className="flex items-center gap-3">
                        <Library className="h-5 w-5 text-safety" />

                        <div>
                          <p className="font-bold text-navy">
                            {bank.title}
                          </p>

                          <p className="mt-1 text-xs text-slate-500">
                            {
                              bank.question_count
                            }{" "}
                            active questions
                          </p>
                        </div>
                      </div>

                      {bank.description && (
                        <p className="mt-3 text-sm leading-relaxed text-slate-600">
                          {
                            bank.description
                          }
                        </p>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-600">
                    No question banks yet.
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}